import countersData from '../data/counters.json';
import heroesData from '../data/heroes.json';

export const findBestCounters = (enemies, bannedHeroes, selectedMap, selectedRank, selectedAllies = []) => {
  // Filter out only banned heroes
  const availableHeroes = Object.keys(countersData).filter(
    heroName => !bannedHeroes.some(h => h.name === heroName)
  );

  const counterScores = {
    vanguard: {},
    duelist: {},
    strategist: {}
  };

  // Calculate scores for each available hero
  availableHeroes.forEach(heroName => {
    let score = 0;
    const hero = heroesData.find(h => h.name === heroName);
    if (!hero) return; // Skip if hero not found in heroesData

    // Check if this hero counters any of the enemy heroes or is countered by them
    enemies.forEach(enemy => {
      // Make sure the enemy exists in our counters data
      if (countersData[enemy.name]) {
        // Add a point if this hero is listed as a counter in any role
        if (countersData[enemy.name].vanguardCounters.includes(heroName) ||
            countersData[enemy.name].duelistCounters.includes(heroName) ||
            countersData[enemy.name].strategistCounters.includes(heroName)) {
          score++;
        }
        
        // Subtract a point if this hero is countered by the enemy in any role
        if (countersData[heroName]) {
          if (countersData[heroName].vanguardCounters.includes(enemy.name) ||
              countersData[heroName].duelistCounters.includes(enemy.name) ||
              countersData[heroName].strategistCounters.includes(enemy.name)) {
            score--;
          }
        }
      }
    });


    // Add an extra point if the hero is strong on the selected map
    if (selectedMap && countersData[heroName]?.bestMaps?.includes(selectedMap)) {
      score++;
    }

    // Add points based on rank performance
    if (selectedRank) {
      // Add the metaRank value for the selected rank
      const rankValue = countersData[heroName]?.metaRanks?.[selectedRank] || 0;
      score += rankValue;
    } else {
      // When no rank is selected, add the average of both rank values
      const diamondPlusValue = countersData[heroName]?.metaRanks?.diamond_plus || 0;
      const belowDiamondValue = countersData[heroName]?.metaRanks?.below_diamond || 0;
      const averageRankValue = (diamondPlusValue + belowDiamondValue) / 2;
      score += averageRankValue;
    }

    // Check for synergies with other available heroes
    // First, calculate scores without synergies
    
    // Store the score in the appropriate role category
    if (score > 0) {
      switch(hero.role) {
        case 'VANGUARD':
          counterScores.vanguard[heroName] = score;
          break;
        case 'DUELIST':
          counterScores.duelist[heroName] = score;
          break;
        case 'STRATEGIST':
          counterScores.strategist[heroName] = score;
          break;
        default:
          console.warn(`Unexpected role type: ${hero.role} for hero: ${heroName}`);
          break;
      }
    }
  });

  // Get top heroes for each role with their scores (without synergies)
  const vanguardPicks = getTopHeroes(counterScores.vanguard, 2);
  const duelistPicks = getTopHeroes(counterScores.duelist, 2);
  const strategistPicks = getTopHeroes(counterScores.strategist, 2);
  
  // Get all top heroes in a single array
  const topHeroes = [
    ...vanguardPicks.top.map(hero => hero.name),
    ...duelistPicks.top.map(hero => hero.name),
    ...strategistPicks.top.map(hero => hero.name),
    ...vanguardPicks.tied.map(hero => hero.name),
    ...duelistPicks.tied.map(hero => hero.name),
    ...strategistPicks.tied.map(hero => hero.name)
  ];

  // Now recalculate scores with synergies, considering both top picks and allied heroes
  const finalScores = {
    vanguard: {},
    duelist: {},
    strategist: {}
  };

  // Get allied hero names for synergy checking
  const alliedHeroNames = selectedAllies.map(ally => ally.name);

  availableHeroes.forEach(heroName => {
    const hero = heroesData.find(h => h.name === heroName);
    if (!hero) return;
    
    // Get the base score we calculated earlier
    let score = 0;
    switch(hero.role) {
      case 'VANGUARD':
        score = counterScores.vanguard[heroName] || 0;
        break;
      case 'DUELIST':
        score = counterScores.duelist[heroName] || 0;
        break;
      case 'STRATEGIST':
        score = counterScores.strategist[heroName] || 0;
        break;
      default:
        console.warn(`Unexpected role type: ${hero.role} for hero: ${heroName}`);
        break;
    }
    
    if (score > 0) {
      // Check for synergies with other top picks
      const synergies = countersData[heroName]?.synergies || [];
      const hasSynergyWithTopHero = synergies.some(synergyHero => topHeroes.includes(synergyHero));
      
      // Also check for synergies with allied heroes
      const hasSynergyWithAlliedHero = synergies.some(synergyHero => alliedHeroNames.includes(synergyHero));
      
      // Add a point if there's a synergy with either top heroes or allied heroes
      if (hasSynergyWithTopHero || hasSynergyWithAlliedHero) {
        score++;
      }
      
      // Store the final score
      switch(hero.role) {
        case 'VANGUARD':
          finalScores.vanguard[heroName] = score;
          break;
        case 'DUELIST':
          finalScores.duelist[heroName] = score;
          break;
        case 'STRATEGIST':
          finalScores.strategist[heroName] = score;
          break;
        default:
          console.warn(`Unexpected role type: ${hero.role} for hero: ${heroName}`);
          break;
      }
    }
  });

  // Get top 2 heroes for each role with their final scores
  const finalVanguardPicks = getTopHeroes(finalScores.vanguard, 2);
  const finalDuelistPicks = getTopHeroes(finalScores.duelist, 2);
  const finalStrategistPicks = getTopHeroes(finalScores.strategist, 2);

  return {
    vanguard: finalVanguardPicks.top.map(hero => ({ name: hero.name, score: hero.score })),
    duelist: finalDuelistPicks.top.map(hero => ({ name: hero.name, score: hero.score })),
    strategist: finalStrategistPicks.top.map(hero => ({ name: hero.name, score: hero.score })),
    tiedPicks: [...finalVanguardPicks.tied, ...finalDuelistPicks.tied, ...finalStrategistPicks.tied].map(hero => ({ name: hero.name, score: hero.score }))
  };
};

const getTopHeroes = (scores, count) => {
  const sortedHeroes = Object.entries(scores).sort(([, a], [, b]) => b - a);
  const topHeroes = sortedHeroes.slice(0, count).map(([name, score]) => ({ name, score }));
  const secondScore = sortedHeroes[1]?.[1];
  const tiedHeroes = sortedHeroes
    .slice(count)
    .filter(([, score]) => score === secondScore)
    .map(([name, score]) => ({ name, score }));

  return { top: topHeroes, tied: tiedHeroes };
};